// 管理者・講師共通：講師スケジュール閲覧・編集ページ

import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { getRequest, postRequest } from "../../apiWrapper";
import { getDatesBetween, toJSTDate, formatDate } from "../../utils/formatDate";
import { toast } from "react-toastify";

const periods = [1, 2, 3, 4, 5, 6, 7];
const SESSION_KEY = "hideMorningPeriods";
const weekdays = ["日", "月", "火", "水", "木", "金", "土"];

const AdminTeacherSchedule = ({ userRole, currentTeacherId }) => {
  const isEditable = userRole === "admin";
  const [hideMorning, setHideMorning] = useState(() => {
    return sessionStorage.getItem(SESSION_KEY) === "true";
  });

  // ▼ 追加：表示オプションを localStorage で保持 -----------------
  const [wrapByDay, setWrapByDay] = useState(() => {
    return localStorage.getItem("wrapByDay") === "true";
  });
  const [onlyFilledPeriods, setOnlyFilledPeriods] = useState(() => {
    return localStorage.getItem("onlyFilledPeriods") === "true";
  });

  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [unavailTeachers, setUnavailTeachers] = useState([]);
  const [unavailStudents, setUnavailStudents] = useState([]);
  const [weeklyShifts, setWeeklyShifts] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [scheduleMap, setScheduleMap] = useState({});
  const [dates, setDates] = useState([]);
  const [entryWindow, setEntryWindow] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [shiftPreset, setShiftPreset] = useState([]);
  const [regularHolidays, setRegularHolidays] = useState([]);
  const [specialHolidays, setSpecialHolidays] = useState([]);
  const [unappliedMessage, setUnappliedMessage] = useState("");
  const [forceInputEnabled, setForceInputEnablPed] = useState(false);
  const [conflictKeys, setConflictKeys] = useState(new Set());
  const [presetStartDate, setPresetStartDate] = useState("");
  const [presetEndDate, setPresetEndDate] = useState("");
  const [overwriteEmptyPreset, setOverwriteEmptyPreset] = useState(false);

  useEffect(() => {
    if (userRole === "teacher" && teachers.length > 0) {
      const myself = teachers.find(t => t.id === currentTeacherId);
      if (myself) setSelectedTeacher(myself.id);
    }
  }, [teachers, userRole, currentTeacherId]);

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [teacherRes, studentRes, subjectRes, windowRes, regRes, specRes] = await Promise.all([
          getRequest("teachers?excludeRetired=true"),
          getRequest("students?excludeWithdrawn=true"),
          getRequest("subjects"),
          getRequest("shiftEntryWindows"),
          getRequest("schoolRegularHolidays"),
          getRequest("schoolSpecialHolidays")
        ]);

        setTeachers(teacherRes.teachers || []);
        setStudents(studentRes.students || []);
        setSubjects(subjectRes.subjects || []);
        setRegularHolidays(regRes.holidays || []);
        setSpecialHolidays(specRes.holidays || []);

        const wins = windowRes.windows || [];
        if (wins.length > 0) {
          wins.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
          const latest = wins[0];
          setEntryWindow(latest);
          const dlist = getDatesBetween(latest.start_date, latest.end_date);
          setDates(dlist);
        }
      } catch (err) {
        console.error("初期データ取得エラー:", err);
        toast.error("初期データ取得に失敗しました");
      }
    };
    fetchInitial();
  }, []);

  useEffect(() => {
    const teacherid = isEditable ? selectedTeacher : currentTeacherId;
    if (!teacherid || !entryWindow) return;
    const fetchRestrictions = async () => {
      try {
        const [ut, us] = await Promise.all([
          getRequest("teacherUnavailabilities/all"),
          getRequest("studentUnavailabilities/all")
        ]);
        setUnavailTeachers(ut.unavailabilities || []);
        setUnavailStudents(us.unavailabilities || []);
      } catch (err) {
        console.error("出欠制限取得エラー:", err);
      }
    };
    fetchRestrictions();
  }, [selectedTeacher, currentTeacherId, entryWindow, isEditable]);

  const handleTeacherSelect = async (teacherid) => {
    setSelectedTeacher(teacherid);
    await fetchScheduleAndShift(teacherid);
  };

  const handleConflictCheck = async () => {
    if (!entryWindow || !selectedTeacher) return;
    const start = entryWindow.start_date;
    const end = entryWindow.end_date;

    try {
      const res = await getRequest(`lessonSchedules?start=${start}&end=${end}`);
      const allSchedules = res.schedules || [];

      // date_period => Set of studentids（他講師の生徒IDのみ集計）
      const existingMap = {};

      allSchedules.forEach(item => {
        if (item.teacherid === selectedTeacher) return; // 👈 自分のスケジュールは除外

        const key = `${formatDate(item.date)}_${item.period}`;
        if (!existingMap[key]) existingMap[key] = new Set();
        if (item.student1id) existingMap[key].add(item.student1id);
        if (item.student2id) existingMap[key].add(item.student2id);
      });

      // 自分の入力と照らし合わせて、重複しているkeyを収集
      const conflicts = new Set();

      Object.entries(scheduleMap).forEach(([key, val]) => {
        const students = [val.student1id, val.student2id].filter(Boolean);
        const existingSet = existingMap[key];
        if (!existingSet) return;

        for (const sid of students) {
          if (existingSet.has(sid)) {
            conflicts.add(key);
            break;
          }
        }
      });

      setConflictKeys(conflicts);
      if (conflicts.size > 0) {
        toast.warn("重複しているコマがあります。背景が赤く表示されます。");
      } else {
        toast.success("重複は見つかりませんでした。");
      }

    } catch (err) {
      console.error("重複チェック失敗:", err);
      toast.error("重複チェックに失敗しました");
    }
  };


  const fetchScheduleAndShift = async (teacherid) => {
    try {
      const shiftRes = await getRequest(`teacherWeeklyShifts?uid=${teacherid}`);
      setWeeklyShifts(shiftRes.shifts || []);

      const start = entryWindow.start_date;
      const end = entryWindow.end_date;
      const res = await getRequest(`lessonSchedules?teacherid=${teacherid}&start=${start}&end=${end}`);
      const map = {};
      res.schedules.forEach(item => {
        const key = `${formatDate(item.date)}_${item.period}`;
        map[key] = {
          student1id: item.student1id || "",
          subject1id: item.subject1id || "",
          student2id: item.student2id || "",
          subject2id: item.subject2id || ""
        };
      });
      setScheduleMap(map);
    } catch (err) {
      console.error("スケジュール取得エラー:", err);
      toast.error("スケジュールの取得に失敗しました");
    }
  };

  useEffect(() => {
    if (!entryWindow) return;
    const teacherid = isEditable ? selectedTeacher : currentTeacherId;
    if (teacherid) {
      fetchScheduleAndShift(teacherid);
    }
  }, [entryWindow, currentTeacherId, isEditable]);

  const handleChange = (date, period, field, value) => {
    if (!isEditable) return;
    const key = `${date}_${period}`;
    setScheduleMap(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const applyPreset = () => {
    const map = { ...scheduleMap };
    const skipped = [];

    dates.forEach(date => {
      // 反映期間のチェック（指定されている場合のみ範囲外をスキップ）
      if (presetStartDate && date < presetStartDate) return;
      if (presetEndDate && date > presetEndDate) return;

      const dow = new Date(date).getDay();
      periods.forEach(period => {
        const shiftKey = `${dow}_${period}`;
        const key = `${date}_${period}`;
        const preset = shiftPreset[shiftKey];
        const shift = weeklyShifts.find(
          ws => ws.teacherid === selectedTeacher && ws.day_of_week === dow && ws.period === period
        );

        // 【修正】講師の勤務枠(shift)が存在しないコマのみスキップ
        if (!shift) return;

        // 休日チェック
        if (isHoliday(date, period)) return;

        // preset が undefined の場合は空オブジェクト {} として扱う
        const currentPreset = preset || {};
        const hasStudent = Boolean(currentPreset.student1id || currentPreset.student2id);

        // 【修正】未入力反映が無効 (false) かつ 生徒が未入力の場合はスキップ
        if (!overwriteEmptyPreset && !hasStudent) return;

        const isUnavailable = isTeacherUnavailable(selectedTeacher, date, period);

        // 出勤不可＋生徒ありの場合のみ警告スキップ対象
        if (isUnavailable && hasStudent) {
          skipped.push(`${date}・${period}講`);
          return;
        }

        // プリセットの内容（未操作・未選択の場合は空文字）で上書き
        map[key] = {
          student1id: currentPreset.student1id || "",
          subject1id: currentPreset.subject1id || "",
          student2id: currentPreset.student2id || "",
          subject2id: currentPreset.subject2id || ""
        };
      });
    });

    setScheduleMap(map);
    setModalOpen(false);

    if (skipped.length > 0) {
      setUnappliedMessage(`以下のコマは講師の出勤不可のため反映できませんでした：${skipped.join("、")}`);
    } else {
      setUnappliedMessage("");
      toast.success("固定シフトを反映しました");
    }
  };

  const isStudentUnavailable = (studentid, date, period) => {
    return unavailStudents.some(u =>
      studentid === u.studentid &&
      period === u.period &&
      formatDate(date) === toJSTDate(u.date).toISOString().split("T")[0]
    );
  };

  const isTeacherUnavailable = (teacherid, date, period) => {
    return unavailTeachers.some(u =>
      teacherid === u.teacherid &&
      period === u.period &&
      formatDate(date) === toJSTDate(u.date).toISOString().split("T")[0]
    );
  };

  const isHoliday = (date, period) => {
    const dow = new Date(date).getDay();
    const isRegular = regularHolidays.some(h => h.day_of_week === dow && (h.period === null || h.period === period));
    const isSpecial = specialHolidays.some(h => {
      const holidayStr = toJSTDate(h.date).toISOString().split("T")[0];
      return holidayStr === date && (h.period === null || h.period === period);
    });
    return isRegular || isSpecial;
  };

  const handleSave = async () => {
    if (!isEditable || !selectedTeacher) return;
    const payload = Object.entries(scheduleMap).map(([key, value]) => {
      const [date, period] = key.split("_");
      return {
        date,
        period: Number(period),
        ...value
      };
    });
    try {
      await postRequest("lessonSchedules", {
        teacherid: selectedTeacher,
        schedules: payload
      });
      toast.success("スケジュールを保存しました");
    } catch (err) {
      console.error("保存エラー:", err);
      toast.error("保存に失敗しました");
    }
  };

  /* ─────────── ヘルパ ─────────── */
  const getStudentName = id => students.find(s => s.id === id)?.name || "-";

  // const highlightIfIncomplete = (sid, subid) => (sid && !subid) || (!sid && subid) ? "bg-yellow-50" : "";
  const highlightIfIncomplete = (studentid, subjectid) =>
    studentid && !subjectid ? "bg-yellow-50" :
      !studentid && subjectid ? "bg-yellow-50" :
        "";

  const renderWeeklyCalendar = () => {
    const visiblePeriods = hideMorning ? periods.filter(p => p > 3) : periods;

    // ▼ 追加：授業が入っている日だけ抽出 --------------------------
    const filteredDates = onlyFilledPeriods
      ? dates.filter(date =>
        periods.some(p => {
          const e = scheduleMap[`${date}_${p}`];
          return e?.student1id || e?.student2id;
        })
      )
      : dates;

    const byWeeks = [];
    let week = new Array(7).fill(null);
    dates.forEach(date => {
      const d = new Date(toJSTDate(date));
      const dow = d.getDay();
      if (week.every(v => v === null) && dow !== 0) {
        for (let i = 0; i < dow; i++) week[i] = "out";
      }
      week[dow] = date;
      if (dow === 6) {
        byWeeks.push(week);
        week = new Array(7).fill(null);
      }
    });
    if (week.some(d => d)) byWeeks.push(week);

    const seatCountMap = {};
    let oneSeatFilledCount = 0;

    Object.values(scheduleMap).forEach(s => {
      const ids = [s.student1id, s.student2id];
      const filled = ids.filter(Boolean).length;

      ids.forEach(id => {
        if (id) seatCountMap[id] = (seatCountMap[id] || 0) + 1;
      });

      if (filled === 1) oneSeatFilledCount += 1;
    }
    );


    return (
      <div className="space-y-10 pb-32">
        {userRole === "admin" && (
          <div className="mt-2 text-sm">
            <label>
              <input
                type="checkbox"
                className="mr-2"
                checked={forceInputEnabled}
                onChange={(e) => setForceInputEnabled(e.target.checked)}
              />
              出勤不可・休日に関係なく入力を許可する
            </label>
          </div>
        )}

        <div className="mb-4">
          {Object.keys(seatCountMap).length > 0 && (
            <div className="mb-4 text-sm text-gray-700">
              <h2 className="font-bold mb-1">生徒ごとの登録コマ数：</h2>
              <ul className="list-disc list-inside mb-2">
                {students
                  .filter(s => seatCountMap[s.id])
                  .map(s => (
                    <li key={s.id}>{s.name}：{seatCountMap[s.id]}コマ</li>
                  ))}
              </ul>
              <p className="text-sm text-red-700">
                ⚠️ 1:1コマ数：{oneSeatFilledCount}コマ
              </p>
            </div>
          )}

          {unappliedMessage && (
            <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-900 rounded text-sm">
              {unappliedMessage}
            </div>
          )}


          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            <label className="text-sm">
              <input
                type="checkbox"
                className="mr-2"
                checked={hideMorning}
                onChange={(e) => {
                  setHideMorning(e.target.checked);
                  sessionStorage.setItem(SESSION_KEY, e.target.checked);
                }}
              />
              1～3講を非表示
            </label>
            {/* ▼ 追加：折り返し表示／授業日のみ表示 */}
            <label className="text-sm mr-6">
              <input
                type="checkbox"
                className="mr-2"
                checked={wrapByDay}
                onChange={e => {
                  setWrapByDay(e.target.checked);
                  localStorage.setItem("wrapByDay", e.target.checked);
                }}
              />
              1日ごとに折り返し表示
            </label>

            <label className="text-sm mr-6">
              <input
                type="checkbox"
                className="mr-2"
                checked={onlyFilledPeriods}
                onChange={e => {
                  setOnlyFilledPeriods(e.target.checked);
                  localStorage.setItem("onlyFilledPeriods", e.target.checked);
                }}
                disabled={!wrapByDay}
              />
              <span className={wrapByDay ? "" : "text-gray-400"}>授業がある日のみ表示</span>
            </label>
          </div>

          {userRole === "admin" && (
            <button
              onClick={() => {
                setModalOpen(true);
              }}
              className="ml-6 bg-blue-500 text-white px-3 py-1 rounded"
            >
              固定シフトから自動入力
            </button>
          )}
        </div>

        {wrapByDay && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filteredDates.map(date => (
              <div key={date} className="border p-2 rounded shadow">
                <div className="text-sm font-bold mb-2">
                  {new Date(date).getMonth() + 1}/{new Date(date).getDate()}（{weekdays[new Date(date).getDay()]}）
                </div>
                {visiblePeriods.map(p => {
                  const key = `${date}_${p}`;
                  const data = scheduleMap[key] || {};

                  /* 背景色ロジック（週表示と同じ） */
                  const isDisabled = !forceInputEnabled && isTeacherUnavailable(selectedTeacher, date, p);
                  const seatStatus = [data.student1id, data.student2id].filter(Boolean).length;
                  const isConflict = conflictKeys.has(key);
                  const bgColor = isConflict
                    ? "bg-red-200"
                    : isDisabled
                      ? "bg-gray-200"
                      : seatStatus === 2
                        ? "bg-blue-100"
                        : seatStatus === 1
                          ? "bg-green-100"
                          : "bg-white";

                  const readOnly = userRole !== "admin";

                  return (
                    <div key={p} className={`border p-1 rounded shadow mb-2 ${bgColor}`} style={{ minHeight: "70px" }}>
                      <div className="text-center font-semibold text-xs mb-1">{p}講</div>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        {[1, 2].map(n => (
                          <div key={n}>
                            {isEditable ? (
                              <>
                                <select
                                  value={data[`student${n}id`] || ""}
                                  onChange={e => handleChange(date, p, `student${n}id`, e.target.value)}
                                  disabled={readOnly || isDisabled}
                                  className={`w-full mb-1 ${highlightIfIncomplete(data[`student${n}id`], data[`subject${n}id`])}`}
                                >
                                  <option value="">生徒{n}</option>
                                  {students.map(s => (
                                    <option key={s.id} value={s.id} disabled={readOnly || (!forceInputEnabled && isStudentUnavailable(s.id, date, p))}>{s.name}</option>
                                  ))}
                                </select>
                                <select
                                  value={data[`subject${n}id`] || ""}
                                  onChange={e => handleChange(date, p, `subject${n}id`, e.target.value)}
                                  disabled={readOnly || isDisabled}
                                  className={`w-full mb-1 ${highlightIfIncomplete(data[`student${n}id`], data[`subject${n}id`])}`}
                                >
                                  <option value="">科目</option>
                                  {subjects.map(sub => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
                                </select>
                              </>
                            ) : (
                              <div className={`p-1 ${highlightIfIncomplete(data[`student${n}id`], data[`subject${n}id`])}`}>
                                {getStudentName(data[`student${n}id`])}
                                {getStudentName(data[`student${n}id`]) !== "-" && data[`student${n}id`] &&
                                  data[`subject${n}id`] &&
                                  subjects.some(s => s.id === data[`subject${n}id`]) && (
                                    <>
                                      （{subjects.find(s => s.id === data[`subject${n}id`])?.name}）
                                    </>
                                  )}
                              </div>

                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        {userRole === "admin" && (
          <Modal
            isOpen={modalOpen}
            onRequestClose={() => setModalOpen(false)}
            className="bg-white p-4 max-w-5xl mx-auto rounded shadow mt-10 text-sm"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">固定シフトに基づいて生徒を一括登録</h2>

              {/* ▼ 追加：プリセット入力のリセット（クリア）ボタン */}
              <button
                type="button"
                onClick={() => {
                  if (window.confirm("プリセットの入力内容をリセットしますか？")) {
                    setShiftPreset({});
                  }
                }}
                className="px-3 py-1 bg-gray-200 text-gray-700 hover:bg-gray-300 rounded text-xs"
              >
                プリセット入力をクリア
              </button>
            </div>

            {/* ▼ オプション設定エリア（反映期間 ＆ 空枠反映チェック） */}
            <div className="mb-4 p-3 bg-gray-50 border rounded flex flex-wrap items-center gap-4">
              {/* 期間指定 */}
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-gray-700">反映期間：</span>
                <input
                  type="date"
                  value={presetStartDate}
                  onChange={(e) => setPresetStartDate(e.target.value)}
                  className="border rounded p-1 bg-white"
                />
                <span>〜</span>
                <input
                  type="date"
                  value={presetEndDate}
                  onChange={(e) => setPresetEndDate(e.target.value)}
                  className="border rounded p-1 bg-white"
                />
              </div>

              {/* 未入力反映チェックボックス */}
              <label className="flex items-center space-x-1 cursor-pointer font-semibold text-gray-700 ml-auto">
                <input
                  type="checkbox"
                  checked={overwriteEmptyPreset}
                  onChange={(e) => setOverwriteEmptyPreset(e.target.checked)}
                  className="rounded"
                />
                <span>未入力（空）の枠も反映して既存入力をクリアする</span>
              </label>
            </div>

            {/* 固定シフト入力枠（既存部分） */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
              {weeklyShifts.map(ws => (
                <div key={`${ws.day_of_week}_${ws.period}`} className="border rounded p-3">
                  <h3 className="font-semibold mb-2">{weekdays[ws.day_of_week]}曜 {ws.period}講</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {[1, 2].map(n => (
                      <div key={n}>
                        <select
                          value={shiftPreset[`${ws.day_of_week}_${ws.period}`]?.[`student${n}id`] || ""}
                          onChange={e => setShiftPreset(prev => ({
                            ...prev,
                            [`${ws.day_of_week}_${ws.period}`]: {
                              ...prev[`${ws.day_of_week}_${ws.period}`],
                              [`student${n}id`]: e.target.value
                            }
                          }))}
                          className="w-full mb-1 border rounded"
                        >
                          <option value="">生徒{n}</option>
                          {students.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <select
                          value={shiftPreset[`${ws.day_of_week}_${ws.period}`]?.[`subject${n}id`] || ""}
                          onChange={e => setShiftPreset(prev => ({
                            ...prev,
                            [`${ws.day_of_week}_${ws.period}`]: {
                              ...prev[`${ws.day_of_week}_${ws.period}`],
                              [`subject${n}id`]: e.target.value
                            }
                          }))}
                          className="w-full border rounded"
                        >
                          <option value="">科目</option>
                          {subjects.map(sub => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* 下部ボタンエリア */}
            <div className="flex justify-end gap-4 mt-6">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-gray-400 text-white rounded">
                閉じる
              </button>
              <button onClick={applyPreset} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                この内容で反映
              </button>
            </div>
          </Modal>
        )}
        {!wrapByDay && byWeeks.map((week, wi) => (
          <div key={wi}>
            <div className="grid grid-cols-7 text-center text-sm font-bold mb-2">
              {week.map((d, i) => (
                <div key={i}>{d && d !== "out" ? `${new Date(d).getMonth() + 1}/${new Date(d).getDate()}（${weekdays[i]}）` : ""}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2 text-xs">
              {week.map((date, di) => (
                <div key={di} className="space-y-2">
                  {visiblePeriods.map(p => {
                    if (!date || date === "out" || (!forceInputEnabled && isHoliday(date, p))) {
                      return <div key={p} className="border p-1 bg-gray-100 rounded text-gray-400" style={{ minHeight: '70px' }}>{p}講</div>;
                    }
                    const key = `${date}_${p}`;
                    const data = scheduleMap[key] || {};
                    const isDisabled = !forceInputEnabled && isTeacherUnavailable(selectedTeacher, date, p);
                    const readOnly = userRole !== "admin";
                    const seatStatus = [data.student1id, data.student2id].filter(Boolean).length;
                    const isConflict = conflictKeys.has(key);
                    const bgColor = isConflict
                      ? "bg-red-200"
                      : isDisabled
                        ? "bg-gray-200"
                        : seatStatus === 2
                          ? "bg-blue-100"
                          : seatStatus === 1
                            ? "bg-green-100"
                            : "bg-white";

                    return (
                      <div key={key} className={`border p-1 ${bgColor} rounded shadow`} style={{ minHeight: '70px' }}>
                        <div className="text-center font-semibold">{p}講</div>
                        <div className="grid grid-cols-2 gap-1">
                          {[1, 2].map(n => (
                            <div key={n} >
                              <select
                                value={data[`student${n}id`] || ""}
                                onChange={e => handleChange(date, p, `student${n}id`, e.target.value)}
                                disabled={readOnly || isDisabled}
                                className={`w-full mb-1 ${highlightIfIncomplete(data[`student${n}id`], data[`subject${n}id`])}`}
                              >
                                <option value="">生徒{n}</option>
                                {students.map(s => (
                                  <option
                                    key={s.id}
                                    value={s.id}
                                    disabled={readOnly || (!forceInputEnabled && isStudentUnavailable(s.id, date, p))}
                                  >
                                    {s.name}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={data[`subject${n}id`] || ""}
                                onChange={e => handleChange(date, p, `subject${n}id`, e.target.value)}
                                disabled={readOnly || isDisabled}
                                className={`w-full mb-1 ${highlightIfIncomplete(data[`student${n}id`], data[`subject${n}id`])}`}
                              >
                                <option value="">科目</option>
                                {subjects.map(sub => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 pt-20">
      <h1 className="text-2xl font-bold mb-4">
        {userRole === "admin" ? "講師スケジュール登録" : "担当授業スケジュール"}
      </h1>

      {entryWindow && (
        <p className="text-sm text-gray-600 mb-4">
          対象期間：{new Date(entryWindow.start_date).toLocaleDateString("ja-JP", { timeZone: "Asia/Tokyo" })} 〜 {new Date(entryWindow.end_date).toLocaleDateString("ja-JP", { timeZone: "Asia/Tokyo" })}
        </p>
      )}

      {userRole === "admin" && (
        <select
          className="p-2 border rounded mb-6"
          value={selectedTeacher || ""}
          onChange={(e) => handleTeacherSelect(e.target.value)}
        >
          <option value="">講師を選択</option>
          {teachers.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      )}

      {selectedTeacher && renderWeeklyCalendar()}

      {userRole === "admin" && selectedTeacher && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-3 z-50 shadow-md text-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 flex-wrap">

            {/* 左：講師選択・対象期間 */}
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <label className="mr-2 font-semibold">講師：</label>
                <select
                  className="p-1 border rounded"
                  value={selectedTeacher}
                  onChange={(e) => handleTeacherSelect(e.target.value)}
                >
                  <option value="">選択</option>
                  {teachers.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <span className="font-semibold">期間：</span>
                {new Date(entryWindow.start_date).toLocaleDateString("ja-JP", { timeZone: "Asia/Tokyo" })} 〜{" "}
                {new Date(entryWindow.end_date).toLocaleDateString("ja-JP", { timeZone: "Asia/Tokyo" })}
              </div>
            </div>

            {/* 中央：チェックボックスと自動入力 */}
            <div className="flex items-center gap-4 flex-wrap">
              <label>
                <input
                  type="checkbox"
                  className="mr-1"
                  checked={hideMorning}
                  onChange={(e) => {
                    setHideMorning(e.target.checked);
                    sessionStorage.setItem(SESSION_KEY, e.target.checked);
                  }}
                />
                1～3講を非表示
              </label>
              <label>
                <input
                  type="checkbox"
                  className="mr-1"
                  checked={forceInputEnabled}
                  onChange={(e) => setForceInputEnabled(e.target.checked)}
                />
                制限無視で入力
              </label>
              <button
                onClick={() => setModalOpen(true)}
                className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
              >
                固定シフトから自動入力
              </button>
            </div>

            {/* 右：コマ数＋ボタン */}
            <div className="flex items-center gap-4 flex-wrap">
              {/* 登録コマ数 */}
              <div className="text-sm text-gray-800">
                {(() => {
                  const seatCountMap = {};
                  let oneSeatFilledCount = 0;
                  Object.values(scheduleMap).forEach(s => {
                    const ids = [s.student1id, s.student2id];
                    const filled = ids.filter(Boolean).length;
                    ids.forEach(id => {
                      if (id) seatCountMap[id] = (seatCountMap[id] || 0) + 1;
                    });
                    if (filled === 1) oneSeatFilledCount += 1;
                  });

                  return (
                    <>
                      <span className="font-semibold">生徒別コマ数：</span>
                      {students.filter(s => seatCountMap[s.id]).map(s => (
                        <span key={s.id} className="mr-2">
                          {s.name}:{seatCountMap[s.id]}コマ
                        </span>
                      ))}
                      {oneSeatFilledCount > 0 && (
                        <span className="text-red-600 ml-2">1:1コマ {oneSeatFilledCount}件</span>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* ボタン */}
              <button
                onClick={handleConflictCheck}
                className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
              >
                重複チェック
              </button>
              <button
                onClick={handleSave}
                className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700"
              >
                保存する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeacherSchedule;
